import { useEffect, useState } from "react";

/**
 * Sticky table of contents for the report.
 *
 * - Desktop (lg+): vertical sidebar on the right of the report container.
 * - Mobile: horizontal scrollable pill nav sticky below the navbar.
 *
 * Sections are dynamically detected from data-toc-section attributes — only
 * the ones that actually render appear here.
 *
 * No AI, no API. Pure frontend state.
 */
const SECTION_ORDER = [
    ["summary", "Summary"],
    ["top-priorities", "Top Priorities"],
    ["readiness", "Readiness"],
    ["deal-risk", "Deal Risk"],
    ["exposure", "Repair Exposure"],
    ["controls", "Search & Filters"],
    ["issues", "Issues"],
    ["not-panic", "Calm Context"],
    ["premium", "Negotiation"],
    ["specialists", "Specialists"],
    ["questions", "Questions"],
    ["quotes", "Quotes"],
];

export default function ReportTOC({ availableIds = [] }) {
    const [activeId, setActiveId] = useState(null);
    const ids = SECTION_ORDER.filter(([id]) => availableIds.includes(id));

    useEffect(() => {
        if (ids.length === 0) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    const id = visible[0].target.getAttribute("data-toc-section");
                    if (id) setActiveId(id);
                }
            },
            { rootMargin: "-100px 0px -60% 0px", threshold: [0, 0.25] },
        );
        const els = ids
            .map(([id]) => document.querySelector(`[data-toc-section="${id}"]`))
            .filter(Boolean);
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [ids.length]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (ids.length < 3) return null;  // not worth showing for very short reports

    const handleClick = (id) => (e) => {
        e.preventDefault();
        const el = document.querySelector(`[data-toc-section="${id}"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            setActiveId(id);
        }
    };

    return (
        <>
            {/* Mobile: horizontal pill nav, sticky below navbar */}
            <nav
                className="lg:hidden sticky top-2 z-30 -mx-4 sm:mx-0"
                data-testid="report-toc-mobile"
            >
                <div className="overflow-x-auto px-4 sm:px-0 py-2 backdrop-blur bg-bl-paper/80 border-y border-bl">
                    <div className="flex items-center gap-2 w-max">
                        {ids.map(([id, label]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                onClick={handleClick(id)}
                                className={
                                    "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition " +
                                    (activeId === id
                                        ? "bg-bl-primary text-white"
                                        : "bg-bl-surface border border-bl text-bl-muted hover:text-bl")
                                }
                                data-testid={`report-toc-mobile-${id}`}
                            >
                                {label}
                            </a>
                        ))}
                    </div>
                </div>
            </nav>

            {/* Desktop: sticky right sidebar */}
            <aside
                className="hidden lg:block fixed top-28 right-6 xl:right-10 z-20 w-44"
                data-testid="report-toc-desktop"
            >
                <div className="bl-card p-4 backdrop-blur bg-bl-paper/85">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium mb-3">
                        Jump to
                    </div>
                    <ul className="space-y-1">
                        {ids.map(([id, label]) => (
                            <li key={id}>
                                <a
                                    href={`#${id}`}
                                    onClick={handleClick(id)}
                                    className={
                                        "block text-sm py-1 px-2 -mx-2 rounded-md transition " +
                                        (activeId === id
                                            ? "text-bl-primary bg-bl-teal-tint font-medium"
                                            : "text-bl-muted hover:text-bl")
                                    }
                                    data-testid={`report-toc-desktop-${id}`}
                                >
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </aside>
        </>
    );
}
